import { EventShim, EventTagerShim } from "../EventTaget/index";
import mutationObserver from "../mutationObserver/index";
import intersectionObserver, { IntersectionHelper } from "../intersectionObserver/index";
import eventCatch from "../eventCatch/index";
import { deepFindLog, isIntersecting, now, fixIosClick , getAttrFrom,findExtraLog, getClickPositionParams, debounce, isUndefined} from "../utils/index";
import { Heatmap, IHeatmapConfig } from "../heatmap/index";

interface wilAttrubuteOption {
  "data-log-view": string;
  "data-log-outview": string;
  "data-log-click": string;
  "data-log-render": string;
  "data-log-extra": string;
  "data-log-view-stay": string;
  [key: string]: string;
}
interface wilOption {
  attribute?: wilAttrubuteOption;
  focusedCallBack?: boolean;
  disableView?: boolean;
  intersectionOptions?: IntersectionObserverInit
  heatmapConfig?: IHeatmapConfig;
  useEnhanceStay?: boolean;
  intersectionObserverDebounced?: {
      wait: number;
      immediate: boolean;
  }
}

const logAttributeMap: wilAttrubuteOption = {
  "data-log-view": "data-log-view",
  "data-log-click": "data-log-click",
  "data-log-extra": "data-log-extra",
  "data-log-render": "data-log-render",
  "data-log-outview": "data-log-outview",
  "data-log-view-stay": "data-log-view-stay"
};

interface sendLog {
  (log: LogEvent): void;
}

type Info = {
  type: string;
  duration?: any;
  time?: any;
  startTime?: any;
  endTime?: any;
};
export class LogEvent {
  log: Object;
  path: Array<Object>;
  target: Element;
  time: number;
  type: string;
  wil?: WilEventTaget
  constructor(target: Element, logPath: Array<Object>, info: Info, wil?: WilEventTaget) {
    this.type = info.type;
    Object.assign(this, info);
    this.log = Object.assign({}, ...[...logPath].reverse());
    this.path = logPath;
    this.target = target;
    this.time = now();
    this.wil = wil;
  }
}

class WilIntersectionObserverEvent extends EventShim {
  from: IntersectionObserverEntry;
  constructor(type: string, event: IntersectionObserverEntry) {
    super(type);
    this.from = event;
  }
}

class WilMutationObserverEvent extends EventShim {
  from: MutationRecord;
  constructor(type: string, event: MutationRecord) {
    super(type);
    this.from = event;
  }
}

class WilClickEventEvent extends EventShim {
  from: MouseEvent;
  constructor(type: string, event: MouseEvent) {
    super(type);
    this.from = event;
  }
}

class WilLogEvent extends EventShim {
  from: LogEvent;
  constructor(type: string, event: LogEvent) {
    super(type);
    this.from = event;
  }
}

class WilEventTaget extends EventTagerShim {
  config: wilAttrubuteOption;
  observedElements: Array<Element>;
  inViewElement: Set<Element>;
  stayWeakMap: WeakMap<Element, any>;
  viewOutWeakSet: WeakSet<Element>;
  outViewWeakSet: WeakSet<Element>;
  intersectionObserver?: IntersectionObserver;
  mutationObserver?: MutationObserver;
  viewWeakSet: WeakSet<Element>;
  options: wilOption;
  enhancedObserver!: IntersectionHelper
  constructor(fn: sendLog, option: wilOption = {}) {
    super();
    this.options = option;
    const { intersectionOptions = {} } = option
    this.observedElements = [];
    this.inViewElement = new Set();
    this.stayWeakMap = new WeakMap();
    this.viewOutWeakSet = new WeakSet();
    this.viewWeakSet = new WeakSet();
    this.outViewWeakSet = new WeakSet();
    this.config = Object.assign({}, logAttributeMap, option.attribute);
    if (!option.disableView) {
        const prev = e => {
            this.dispatchEvent(
                new WilIntersectionObserverEvent("intersectionObserver", e)
            );
        }
        const intersectionObserverCallbacks = [prev]
        // 派发 intersectionObserver
        this.intersectionObserver = intersectionObserver(intersectionObserverCallbacks, intersectionOptions);
        // 派发 mutationObserver
        this.mutationObserver = mutationObserver(e => {
            this.dispatchEvent(new WilMutationObserverEvent("mutationObserver", e));
        });
    }
    const intersectionObserverDebounced = this.options.intersectionObserverDebounced;
    if(!isUndefined(intersectionObserverDebounced)) {
        const { wait = 0, immediate = false } = intersectionObserverDebounced!;
        this.intersectionObserveElements = debounce(observer => this._intersectionObserveElements(observer), wait, immediate);
    }
    this.init(fn);

    // 如果对heatmap进行配置, 则初始化heatmap相关的埋点
    if (option.heatmapConfig) {
      new Heatmap(fn, option.heatmapConfig, this);
    }
  }

  init(fn: sendLog) {

    // 派发 click 事件
    eventCatch(event => {
      this.dispatchEvent(new WilClickEventEvent("clickEvent", event));
    });

    // 监听 intersectionObserver
    this.addEventListener(
      "intersectionObserver",
      (event: WilIntersectionObserverEvent) => {
        this.handleIntersectionObserver(event);
      }
    );
    // 监听 mutationObserver
    this.addEventListener(
      "mutationObserver",
      (event: WilMutationObserverEvent) => {
        this.handleMutationObserver(event);
      }
    );
    // 监听 click
    this.addEventListener("clickEvent", (event: WilClickEventEvent) => {
      this.handleClickEvent(event);
    });

    // 监听 发出的 logEvent
    this.listenLogEvent(fn);

    // 初始化
    this.intersectionObserveElements(this.intersectionObserver);

    // ios click 在 document 上代理 不生效 bug
    this.fixIOSSafari();
  }

  genarateDispatchEvent(
    target: Element,
    log: Object[],
    params: { type: string; [key: string]: any }
  ) {
    this.dispatchEvent(
      new WilLogEvent("logEvent", new LogEvent(target, log, params, this))
    );
  }

  handleIntersectionObserver(event: WilIntersectionObserverEvent) {
    const e = event.from;
    const { target } = e;
    // 进出视图事件 view outview stayview

    // view
    if (isIntersecting(e) && !this.viewWeakSet.has(target)) {
      if (this.dispatchViewLog(target)) {
          try {
            const config = JSON.parse(getAttrFrom(e, 'data-log-view-config') || '{}')
            if (!config['repeated']) {
                this.viewWeakSet.add(target);
            }
        }catch(e) {
            this.viewWeakSet.add(target);
        }
      }
    }
    // stay 进入保存时间
    // 离开 发出事件
    const attributeName = this.config["data-log-view-stay"];
    const attributeExtraName = this.config["data-log-extra"];
    if (isIntersecting(e) && target.hasAttribute(attributeName)) {
      this.stayWeakMap.set(target, now());
      this.inViewElement.add(target);
    }
    if (!isIntersecting(e) && target.hasAttribute(attributeName)) {
      const startTime = this.stayWeakMap.get(target);
      this.inViewElement.delete(target);
      if (startTime) {
        const logJson = deepFindLog(target, attributeName, attributeExtraName);
        if (logJson) {
          this.genarateDispatchEvent(target, logJson, {
            type: "view-stay",
            duration: now() - startTime,
            startTime,
            endTime: now()
          });
        }
      }
    }

    // out-view
    if (true) {
      // 块作用域
      const attributeName = this.config["data-log-outview"];
      const attributeExtraName = this.config["data-log-extra"];
      if (isIntersecting(e) && target.hasAttribute(attributeName)) {
        this.viewOutWeakSet.add(target);
      }
      if (
        !isIntersecting(e) &&
        !this.outViewWeakSet.has(target) &&
        this.viewOutWeakSet.has(target)
      ) {
        const log = deepFindLog(target, attributeName, attributeExtraName);
        if (log) {
          this.viewOutWeakSet.delete(target);
          this.outViewWeakSet.add(target);
          this.genarateDispatchEvent(target, log, {
            type: "outview"
          });
        }
      }
    }
  }

  fixIOSSafari() {
    if (document.body) {
      fixIosClick();
    } else {
      document.addEventListener("DOMContentLoaded", () => {
        fixIosClick();
      });
    }
  }

  intersectionObserveElements(intersectionObserver?: IntersectionObserver) {
      this._intersectionObserveElements(intersectionObserver);
    }
    
    _intersectionObserveElements(intersectionObserver?: IntersectionObserver) {
      console.log('intersectionObserveElements!!!')
    const aboutViewAttributeName: Array<string> = [
        "data-log-view",
        "data-log-view-stay",
        "data-log-outview"
      ];
      aboutViewAttributeName
        .map(key => this.config[key])
        .filter(i => !!i)
        .map(attributeName => document.querySelectorAll(`[${attributeName}]`))
        .forEach(eles => {
          for (let i = 0; i < eles.length; i++) {
            if (intersectionObserver) {
              intersectionObserver.observe(eles[i]);
            }
          }
        });
  }

  handleMutationObserver(event: WilMutationObserverEvent) {
    const e = event.from;
    const attributeName = this.config["data-log-render"];
    const extraAttribute = this.config["data-log-extra"];
    if (e.type === "childList" && e.addedNodes && attributeName) {
      const addedNodes = e.addedNodes;
      for (let i = 0; i < addedNodes.length; i++) {
        const currentNode = addedNodes.item
          ? addedNodes.item(i)
          : addedNodes[i];
        if (
          currentNode &&
          currentNode.nodeType === 1 &&
          currentNode instanceof Element
        ) {
          const items = currentNode.querySelectorAll(`[${attributeName}]`);
          if (currentNode.hasAttribute(attributeName)) {
            const log = deepFindLog(currentNode, attributeName, extraAttribute); //getAttributeObjectValue(currentNode, attributeName);
            if (log) {
              this.genarateDispatchEvent(currentNode, log, { type: "render" });
            }
          }
          for (let i = 0; i < items.length; i++) {
            const log = deepFindLog(items[i], attributeName, extraAttribute);
            if (log) {
              this.genarateDispatchEvent(items[i], log, { type: "render" });
            }
          }
        }
      }
    }
    this.intersectionObserveElements(this.intersectionObserver);
    this.fixIOSSafari();
  }

  handleClickEvent(e: WilClickEventEvent) {
    const event = e.from;
    this.handleMouseEvent(event);
  }

  handleMouseEvent(e: MouseEvent) {
    const targetElement = e.target;

    if (
      targetElement &&
      (targetElement instanceof HTMLElement ||
        targetElement instanceof SVGElement) &&
      targetElement.nodeType === 1 &&
      e.type == "click"
    ) {
      const attributeName = this.config["data-log-click"];
      const attributeExtraName = this.config["data-log-extra"];
      const log = deepFindLog(targetElement, attributeName, attributeExtraName);

      if (log !== null) {
        // 添加位置信息等参数
        const params = getClickPositionParams(e, log);
        params ? log.unshift({ ...params }) : log
        this.genarateDispatchEvent(targetElement, log, { type: "click" });
      }
    }
  }

  listenLogEvent(fn: sendLog) {
    this.addEventListener("logEvent", (e: WilLogEvent) => {
      const event = e.from;
      fn(event);
    });
  }

  focusCallBack = () => {
    this.updateStayTime();
  };

  blurCallBack = () => {
    this.dispatchStayLog();
  };

  updateStayTime() {
    this.inViewElement.forEach(ele => {
      this.stayWeakMap.set(ele, now());
    });
  }

  dispatchViewLog(target: Element): boolean {
    const attributeName = this.config["data-log-view"];
    const attributeExtraName = this.config["data-log-extra"];
    const log = deepFindLog(target, attributeName, attributeExtraName);
    // view
    if (log) {
      this.genarateDispatchEvent(target, log, {
        type: "view",
      });
      return true;
    }
    return false;
  }

  dispatchStayLog() {
    this.inViewElement.forEach(ele => {
      const startTime = this.stayWeakMap.get(ele);
      if (startTime) {
        const attributeName = this.config["data-log-view-stay"];
        const attributeExtraName = this.config["data-log-extra"];
        const logJson = deepFindLog(ele, attributeName, attributeExtraName);
        if (logJson) {
          this.genarateDispatchEvent(ele, logJson, {
            type: "view-stay",
            duration: now() - startTime,
            startTime,
            endTime: now()
          });
        }
      }
    });
  }
}

export { WilEventTaget, deepFindLog, IntersectionHelper };
function Wil(fn: sendLog, option: wilOption = {}) {
  return new WilEventTaget(fn, option);
}
Wil.utils = {
    findExtraLog
}
export default Wil
